import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/learner/ui/Button'
import { useFocusTrap } from '@/hooks/useFocusTrap'

export interface ExitConfirmDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  title?: string
  message?: string
}

export function ExitConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
}: ExitConfirmDialogProps) {
  const { t } = useTranslation('learner')
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useFocusTrap(dialogRef, open)

  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onCancel])

  if (!open) return null

  const resolvedTitle = title ?? t('learn.exitDialog.title')
  const resolvedMessage = message ?? t('learn.exitDialog.message')

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-dialog-title"
      aria-describedby="exit-dialog-description"
    >
      <div
        ref={dialogRef}
        className="flex w-full max-w-[360px] flex-col items-start gap-6 rounded-[24px] bg-lc-bg-primary px-8 pt-8 pb-6 shadow-[0_8px_12px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full flex-col items-center gap-3 text-center text-lc-text-primary">
          <h2 id="exit-dialog-title" className="w-full text-lc-v3-h4 font-bold">
            {resolvedTitle}
          </h2>
          <p
            id="exit-dialog-description"
            className="w-full text-lc-v3-body-1 font-normal"
          >
            {resolvedMessage}
          </p>
        </div>
        <div className="flex w-full items-center justify-center gap-[10px]">
          <Button variant="neutral" size="M" onClick={onCancel}>
            {t('learn.exitDialog.cancel')}
          </Button>
          <Button variant="primary" size="M" onClick={onConfirm}>
            {t('learn.exitDialog.confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}
